import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { HomeService } from "@shared/services/home.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import ActionTypesEnum from "@shared/enums/action-constant.enum";
import { CustomEventService } from "@shared/services/custom-event.service";
import { NzModalRef } from "ng-zorro-antd/modal";
import { UserService } from "@core/services/user.service";
import { sortFieldsOnTable } from "@shared/utils/sort.util";
import { Map } from "immutable";
import { AdminService } from "@shared/services/admin.service";
import { StorageService } from "@core/services/storage.service";

@UntilDestroy()
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html'
})
export class HomeComponent implements OnInit,OnDestroy {

  @Input() activeUser: ActiveUser.ActiveUserModel;

  loading: boolean;
  userLoading: boolean;
  scorecardLoading: boolean;
  dashboardLoading: boolean;
  repositoriesLoading: boolean;
  userData: any;
  userTeamsData: any;
  userAccountsData: any;
  scorecardsTeamsData: any;
  agileSpaceTeamsData: any[];
  scorecardsData: any;
  dashboardsData: any;
  repositoriesData: any;
  agileSpaceData: any;
  agileSpaceLoading: boolean;
  actionsData: any;
  updateModal: NzModalRef;
  searchValue = '';
  awaitingTableData: any;
  awaitingActionsDataLoading: boolean;
  latestTableData: Map<string, any> = Map();
  latestTableDataLoading: boolean;

  latestActivitiesList: Base.KeyLabelModel[] = [
    {
      label: 'AgileSpace',
      key: 'AGILE_SPACE'
    },
    {
      label: 'Gitwiser',
      key: 'PULL_REQUEST_ANALYTICS'
    }
  ];

  awaitingActionsList: Base.KeyLabelModel[] = [
    {
      label: 'PR Review',
      key: 'PR_REVIEW'
    },
    {
      label: 'PR Submit',
      key: 'PR_SUBMIT'
    },
    {
      label: 'Task in Progress',
      key: 'TASK_IN_PROGRESS'
    }
  ];

  selectedLatestActivities: string;
  selectedAwaitingActions: string;

  individualScorecard: any;
  featuresData: any;

  constructor(private customEventService: CustomEventService,
              private homeService: HomeService,
              private adminService: AdminService,
              public storageService: StorageService,
              public userService: UserService) {
    this.activeUser = this.userService.activeUser;
  }

  ngOnInit(): void {
    this.getUserData();
    this.getDashbordsData();
    this.getScorecardsData();
    this.getIndividualScorecardActive();

    this.customEventService.eventAnnounced$.pipe(untilDestroyed(this))
      .subscribe(({ action, data }) => {
        if (action === ActionTypesEnum.CLOSE_MODAL) {
          if (this.updateModal) {
            this.updateModal.close();
          }
        }

      });

  }

  getUserData(): void {
    this.userLoading = true;
    this.homeService.listProfile().pipe(untilDestroyed(this)).subscribe(response => {
      this.userData = response;
      if(!response.id){
        this.userLoading = false;
        return
      }
      this.userTeamsData = response.teams?.map(item => item.name);
      this.userAccountsData = response.addonAccounts?.map(item => item);
      this.userLoading = false
    }, error => {
      this.userLoading = false;
      console.log('error occurred while fetching initial data for home data', error);
    })
  }

  getScorecardsData(): void {
    this.scorecardLoading = true;
    this.homeService.listScorecards().pipe(untilDestroyed(this)).subscribe(response => {
      this.scorecardsTeamsData = response.teamScoreCards;
      this.scorecardsData = response;
      this.scorecardLoading = false
    }, error => {
      this.scorecardLoading = false;
      console.log('error occurred while fetching initial data for home data', error);
    })
  }

  getDashbordsData(): void {
    this.dashboardLoading = true;
    this.homeService.listDashboards().pipe(untilDestroyed(this)).subscribe(response => {
      this.dashboardsData = response;
      this.dashboardLoading = false
    }, error => {
      this.dashboardLoading = false;
      console.log('error occurred while fetching initial data for home data', error);
    })
  }

  getRepositoriesData(): void {
    this.repositoriesLoading = true;
    this.homeService.listRepositories().pipe(untilDestroyed(this)).subscribe(response => {
      this.repositoriesData = response;
      this.repositoriesLoading = false
    }, error => {
      this.repositoriesLoading = false;
      console.log('error occurred while fetching initial data for home data', error);
    })
  }

  getAgileData(): void {
    this.agileSpaceLoading = true;
    this.homeService.listAgileSpace().pipe(untilDestroyed(this)).subscribe(response => {
      this.agileSpaceData = response;
      this.agileSpaceTeamsData = response.map(item => item.teamResource);
      this.agileSpaceLoading = false
    }, error => {
      this.agileSpaceLoading = false;
      console.log('error occurred while fetching initial data for home data', error);
    })
  }

  getLastActivitiesData(): void {
    this.latestTableDataLoading = true;
    this.homeService.listLastActivities().pipe(untilDestroyed(this)).subscribe(response => {
      this.latestTableData = response;
      this.selectedLatestActivities = response;
      this.latestTableDataLoading = false
    }, error => {
      this.latestTableDataLoading = false;
      console.log('error occurred while fetching initial data for home data', error);
    })
  }

  getAwaitingActionsData(): void {
    this.awaitingActionsDataLoading = true;
    this.homeService.listActions().pipe(untilDestroyed(this)).subscribe(response => {
      this.awaitingTableData = response;
      this.selectedAwaitingActions = response;
      this.awaitingActionsDataLoading = false

    },error => {
      this.awaitingActionsDataLoading = false;
      console.log('error occurred while fetching initial data for home data', error);
    })

  }

  sort(e): void {
    const data = this.awaitingTableData;

    if (!e.value && !this.searchValue) {
      this.awaitingTableData = data;
      return;
    }

    this.awaitingTableData = sortFieldsOnTable(this.awaitingTableData, e);
  }

  search(key: string): void {
    this.latestTableDataLoading = true;
       this.homeService.listLastActivities().pipe(untilDestroyed(this)).subscribe(response => {
         this.latestTableData = response.filter(item => item.activityType === key).map
         (item => item);
         this.selectedLatestActivities = response.filter(item => item.activityType === key).map
         (item => item)[0]?.activityType

         if(this.selectedLatestActivities === undefined) {
           this.selectedLatestActivities  = key;
         }

         this.latestTableDataLoading = false
    },)
  }

  awaitingActionsSearch(key: string): void {
    this.awaitingActionsDataLoading = true;
    this.homeService.listActions().pipe(untilDestroyed(this)).subscribe(response => {
      this.awaitingTableData = response.filter(item => item.actionTypeEnum === key).map
      (item => item);
      this.selectedAwaitingActions = response.filter(item => item.actionTypeEnum === key).map
      (item => item)[0]?.actionTypeEnum

      if(this.selectedAwaitingActions === undefined) {
        this.selectedAwaitingActions  = key;
      }

      this.awaitingActionsDataLoading = false
    },)
  }
  getIndividualScorecardActive(): void {
    this.individualScorecard  = JSON.parse(this.storageService.get('features_data_individual'));
    if (this.individualScorecard === null) {
      this.adminService.getFeatures()
        .pipe(untilDestroyed(this))
        .subscribe((response: AdminSettings.BaseAdminModel<AdminSettings.Features>) => {
          this.featuresData = response;
          this.individualScorecard = this.featuresData.find(item => item.key === 'INDIVIDUAL_SCORECARD').active;
          this.loading = false;
        }, error => {
          this.loading = false;
          console.log('error while fetching values', error);
        });
    }
  }
  ngOnDestroy() {
  }
}
